<template>
  <div
    :class="[
      $style.base,
      $style[size],
      [$style['color-' + color]],
      {
        [$style.border]: border,
        [$style.hasIcon]: !!icon,
        [$style.noWrap]: noWrap,
        [$style.center]: center,
        [$style.inline]: inline,
        [$style.hasMaxLines]: !!maxLines
      }
    ]"
    :style="maxLines ? { '-webkit-line-clamp': maxLines, 'line-clamp': maxLines } : undefined"
  >
    <BaseIcon
      v-if="icon"
      :name="typeof icon === 'string' ? icon : icon.name"
      :color="typeof icon === 'string' ? 'default' : icon.color"
      :size="iconSize"
    />
    <slot />
    <div
      v-if="info"
      :class="$style.info"
    >
      <BaseIcon
        name="info"
        size="s"
        :tooltip="{
          text: info,
          touch: true
        }"
      />
    </div>
  </div>
</template>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<script
  lang="ts"
  setup
>
import { computed } from 'vue';
import type { IconName } from '@/components/base-icon/types';

const props = withDefaults(
  defineProps<{
    color?: 'default' | 'inverted' | 'fixed';
    border?: boolean;
    center?: boolean;
    icon?:
      | IconName
      | { name: IconName; color: 'success' | 'warning' | 'error' };
    info?: string;
    inline?: boolean;
    maxLines?: number;
    noWrap?: boolean;
    size?: 'xs' | 's' | 'm' | 'l' | 'xl';
  }>(),
  {
    color: 'default',
    size: 'm',
  },
);

const iconSize = computed(() => {
  switch (props.size) {
    case 'xs':
    case 's':
      return 's';
    case 'l':
    case 'xl':
      return 'l';
    default:
      return 'm';
  }
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  align-items: center;
  gap: $spacing * 0.25;

  &.hasIcon {
    gap: 6px;
  }

  // xs and s are the uppercase overlines
  &.xs,
  &.s {
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: bold;

    &.color-default {
      color: var(--text-secondary);
    }
  }

  &.xs {
    font-size: 10px;
  }

  &.s {
    font-size: 12px;
  }

  &.m,
  &.l,
  &.xl {
    font-weight: bold;
  }

  &.m {
    font-size: 16px;
  }

  &.l {
    font-size: 20px;
  }

  &.xl {
    font-size: 24px;
  }

  &.color-inverted {
    color: var(--color-text-inverted);
  }

  &.color-fixed {
    color: var(--text-fixed);
  }

  &.inline {
    display: inline-block;
  }

  &.border {
    padding-bottom: $spacing;
    border-bottom: 1px solid var(--border);
  }

  &.noWrap {
    white-space: nowrap;
  }

  &.center {
    justify-content: center;
    text-align: center;
  }

  &.hasMaxLines {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.info {
  display: inline-flex;
}
</style>
